/**
 * Leadership insights charts — Calendar app only.
 * User can select source: All (combined), CMS Calendar, Memos, or CAP.
 * Combines where it aids totals/composition; keeps separate for accountability (different dimensions).
 */
import React, { useMemo, useState } from 'react';
import { Bar, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

const SOURCE_LABELS = { calendar: 'CMS Calendar', memo: 'Memos', cap: 'CAP' };
const STATUS_ORDER = ['overdue', 'due_soon', 'upcoming', 'in_progress', 'completed', 'no_action_needed'];
const STATUS_LABELS = {
  overdue: 'Overdue',
  due_soon: 'Due soon',
  upcoming: 'Upcoming',
  in_progress: 'In progress',
  completed: 'Completed',
  no_action_needed: 'No action needed',
};
const STATUS_COLORS = {
  overdue: 'rgb(185, 28, 28)',
  due_soon: 'rgb(180, 83, 9)',
  upcoming: 'rgb(37, 99, 235)',
  in_progress: 'rgb(14, 165, 233)', // sky-500
  completed: 'rgb(100, 116, 139)', // slate-500 — greyed out / closed
  no_action_needed: 'rgb(71, 85, 105)',
};
const SOURCE_CHART_COLORS = { calendar: 'rgba(37, 99, 235, 0.88)', memo: 'rgba(124, 58, 237, 0.88)', cap: 'rgba(16, 185, 129, 0.88)' };
const BAR_RADIUS = 8;

function getAssigneeLabel(item) {
  if (item.source === 'memo') return item.owner_name || item.owner_email || 'Unassigned';
  if (item.source === 'cap') return item.owner_name || item.owner_email || 'Unassigned';
  if (!item.assigned_to_user_id) return 'Unassigned';
  return item.assignee_name || item.assignee_email || 'Assigned';
}

function getMemoDepartmentLabels(item) {
  if (item.source !== 'memo') return [];
  const depts = Array.isArray(item.departments) ? item.departments : [];
  if (depts.length === 0) return ['No department'];
  return depts.map((d) => (d && String(d).trim()) || 'Other');
}

export default function CalendarCharts({
  filteredItems: parentFilteredItems,
  obligations = [],
  memos = [],
  caps = [],
  getEffectiveStatus,
  DUE_SOON_DAYS = 14,
}) {
  const [sourceFilter, setSourceFilter] = useState('all'); // 'all' | 'calendar' | 'memo' | 'cap'

  /** Calendar only: non-actionable obligations are excluded from all chart metrics. */
  const obligationsWithSource = useMemo(
    () => (obligations || []).filter((o) => o.actionable !== false).map((o) => ({ ...o, source: 'calendar' })),
    [obligations]
  );
  const memosWithSource = useMemo(
    () => (memos || []).map((m) => ({ ...m, source: 'memo' })),
    [memos]
  );
  const capsWithSource = useMemo(
    () => (caps || []).map((c) => ({ ...c, source: 'cap' })),
    [caps]
  );

  /** Use parent-filtered items when provided (sync with main filters); else filter by source locally. */
  const filteredItems = useMemo(() => {
    const fromParent = Array.isArray(parentFilteredItems) ? parentFilteredItems : null;
    if (fromParent) {
      if (sourceFilter === 'all') return fromParent;
      return fromParent.filter((i) => (i.source || '').toLowerCase() === sourceFilter);
    }
    if (sourceFilter === 'calendar') return obligationsWithSource;
    if (sourceFilter === 'memo') return memosWithSource;
    if (sourceFilter === 'cap') return capsWithSource;
    return [...obligationsWithSource, ...memosWithSource, ...capsWithSource];
  }, [parentFilteredItems, sourceFilter, obligationsWithSource, memosWithSource, capsWithSource]);

  const statusCountsBySource = useMemo(() => {
    const out = { calendar: {}, memo: {}, cap: {} };
    STATUS_ORDER.forEach((s) => { out.calendar[s] = 0; out.memo[s] = 0; out.cap[s] = 0; });
    obligationsWithSource.forEach((item) => {
      const s = getEffectiveStatus(item);
      if (out.calendar[s] !== undefined) out.calendar[s] += 1;
    });
    memosWithSource.forEach((item) => {
      const s = getEffectiveStatus(item);
      if (out.memo[s] !== undefined) out.memo[s] += 1;
    });
    capsWithSource.forEach((item) => {
      const s = getEffectiveStatus(item);
      if (out.cap[s] !== undefined) out.cap[s] += 1;
    });
    return out;
  }, [obligationsWithSource, memosWithSource, capsWithSource, getEffectiveStatus]);

  const statusCountsFiltered = useMemo(() => {
    const counts = {};
    STATUS_ORDER.forEach((s) => { counts[s] = 0; });
    filteredItems.forEach((item) => {
      const s = getEffectiveStatus(item);
      if (counts[s] !== undefined) counts[s] += 1;
    });
    return counts;
  }, [filteredItems, getEffectiveStatus]);

  const byAssignee = useMemo(() => {
    const map = {};
    obligationsWithSource.forEach((o) => {
      const label = getAssigneeLabel(o);
      map[label] = (map[label] || 0) + 1;
    });
    return map;
  }, [obligationsWithSource]);

  const overdueAtRiskByAssignee = useMemo(() => {
    const map = {};
    obligationsWithSource.forEach((o) => {
      const s = getEffectiveStatus(o);
      if (s !== 'overdue' && s !== 'due_soon') return;
      const label = getAssigneeLabel(o);
      if (!map[label]) map[label] = { overdue: 0, due_soon: 0 };
      if (s === 'overdue') map[label].overdue += 1;
      else map[label].due_soon += 1;
    });
    return map;
  }, [obligationsWithSource, getEffectiveStatus]);

  const byAssigneeCap = useMemo(() => {
    const map = {};
    capsWithSource.forEach((o) => {
      const label = getAssigneeLabel(o);
      map[label] = (map[label] || 0) + 1;
    });
    return map;
  }, [capsWithSource]);

  const overdueAtRiskByAssigneeCap = useMemo(() => {
    const map = {};
    capsWithSource.forEach((o) => {
      const s = getEffectiveStatus(o);
      if (s !== 'overdue' && s !== 'due_soon') return;
      const label = getAssigneeLabel(o);
      if (!map[label]) map[label] = { overdue: 0, due_soon: 0 };
      if (s === 'overdue') map[label].overdue += 1;
      else map[label].due_soon += 1;
    });
    return map;
  }, [capsWithSource, getEffectiveStatus]);

  const overdueDueSoonByTypeCalendar = useMemo(() => {
    const map = {};
    obligationsWithSource.forEach((item) => {
      const s = getEffectiveStatus(item);
      if (s !== 'overdue' && s !== 'due_soon') return;
      const type = item.obligation_type || item.type || 'Other';
      if (!map[type]) map[type] = { overdue: 0, due_soon: 0 };
      if (s === 'overdue') map[type].overdue += 1;
      else map[type].due_soon += 1;
    });
    return map;
  }, [obligationsWithSource, getEffectiveStatus]);

  const overdueDueSoonByTypeMemos = useMemo(() => {
    const map = {};
    memosWithSource.forEach((item) => {
      const s = getEffectiveStatus(item);
      if (s !== 'overdue' && s !== 'due_soon') return;
      const type = item.classification || 'Memo';
      if (!map[type]) map[type] = { overdue: 0, due_soon: 0 };
      if (s === 'overdue') map[type].overdue += 1;
      else map[type].due_soon += 1;
    });
    return map;
  }, [memosWithSource, getEffectiveStatus]);

  const overdueDueSoonByTypeCap = useMemo(() => {
    const map = {};
    capsWithSource.forEach((item) => {
      const s = getEffectiveStatus(item);
      if (s !== 'overdue' && s !== 'due_soon') return;
      const type = item.obligation_type || 'CAP';
      if (!map[type]) map[type] = { overdue: 0, due_soon: 0 };
      if (s === 'overdue') map[type].overdue += 1;
      else map[type].due_soon += 1;
    });
    return map;
  }, [capsWithSource, getEffectiveStatus]);

  const memosByDepartment = useMemo(() => {
    const map = {};
    memosWithSource.forEach((item) => {
      getMemoDepartmentLabels(item).forEach((label) => { map[label] = (map[label] || 0) + 1; });
    });
    return map;
  }, [memosWithSource]);

  const memosAtRiskByDepartment = useMemo(() => {
    const map = {};
    memosWithSource.forEach((item) => {
      const s = getEffectiveStatus(item);
      if (s !== 'overdue' && s !== 'due_soon') return;
      getMemoDepartmentLabels(item).forEach((label) => {
        if (!map[label]) map[label] = { overdue: 0, due_soon: 0 };
        if (s === 'overdue') map[label].overdue += 1;
        else map[label].due_soon += 1;
      });
    });
    return map;
  }, [memosWithSource, getEffectiveStatus]);

  const dueByMonthBySource = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const monthKeys = ['Past due'];
    for (let i = 0; i <= 12; i++) {
      const d = new Date(today.getFullYear(), today.getMonth() + i, 1);
      monthKeys.push(d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }));
    }
    const calendar = {};
    const memo = {};
    const cap = {};
    monthKeys.forEach((k) => { calendar[k] = 0; memo[k] = 0; cap[k] = 0; });
    obligationsWithSource.forEach((item) => {
      const due = item.due_date || item.end_date;
      if (!due) return;
      const dueDate = new Date(due);
      dueDate.setHours(0, 0, 0, 0);
      const key = dueDate < today ? 'Past due' : dueDate.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
      if (calendar[key] !== undefined) calendar[key] += 1;
    });
    memosWithSource.forEach((item) => {
      const due = item.due_date;
      if (!due) return;
      const dueDate = new Date(due);
      dueDate.setHours(0, 0, 0, 0);
      const key = dueDate < today ? 'Past due' : dueDate.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
      if (memo[key] !== undefined) memo[key] += 1;
    });
    capsWithSource.forEach((item) => {
      const due = item.due_date;
      if (!due) return;
      const dueDate = new Date(due);
      dueDate.setHours(0, 0, 0, 0);
      const key = dueDate < today ? 'Past due' : dueDate.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
      if (cap[key] !== undefined) cap[key] += 1;
    });
    return { monthKeys, calendar, memo, cap };
  }, [obligationsWithSource, memosWithSource, capsWithSource]);

  const dueByMonthFiltered = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const monthKeys = ['Past due'];
    for (let i = 0; i <= 12; i++) {
      const d = new Date(today.getFullYear(), today.getMonth() + i, 1);
      monthKeys.push(d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }));
    }
    const buckets = {};
    monthKeys.forEach((k) => { buckets[k] = 0; });
    filteredItems.forEach((item) => {
      const due = item.due_date || item.end_date;
      if (!due) return;
      const dueDate = new Date(due);
      dueDate.setHours(0, 0, 0, 0);
      const key = dueDate < today ? 'Past due' : dueDate.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
      if (buckets[key] !== undefined) buckets[key] += 1;
    });
    return { monthKeys, buckets };
  }, [filteredItems]);

  const persistentByTypeCalendar = useMemo(() => {
    const map = {};
    obligationsWithSource.forEach((item) => {
      if (getEffectiveStatus(item) !== 'overdue') return;
      const type = item.obligation_type || item.type || 'Other';
      map[type] = (map[type] || 0) + 1;
    });
    return Object.entries(map).sort((a, b) => b[1] - a[1]).slice(0, 10);
  }, [obligationsWithSource, getEffectiveStatus]);

  const persistentByTypeMemos = useMemo(() => {
    const map = {};
    memosWithSource.forEach((item) => {
      if (getEffectiveStatus(item) !== 'overdue') return;
      const type = item.classification || 'Memo';
      map[type] = (map[type] || 0) + 1;
    });
    return Object.entries(map).sort((a, b) => b[1] - a[1]).slice(0, 10);
  }, [memosWithSource, getEffectiveStatus]);

  const persistentByTypeCap = useMemo(() => {
    const map = {};
    capsWithSource.forEach((item) => {
      if (getEffectiveStatus(item) !== 'overdue') return;
      const type = item.obligation_type || 'CAP';
      map[type] = (map[type] || 0) + 1;
    });
    return Object.entries(map).sort((a, b) => b[1] - a[1]).slice(0, 10);
  }, [capsWithSource, getEffectiveStatus]);

  const showCombined = sourceFilter === 'all';
  const calOverdue = statusCountsBySource.calendar.overdue || 0;
  const calDueSoon = statusCountsBySource.calendar.due_soon || 0;
  const memOverdue = statusCountsBySource.memo.overdue || 0;
  const memDueSoon = statusCountsBySource.memo.due_soon || 0;
  const capOverdue = statusCountsBySource.cap.overdue || 0;
  const capDueSoon = statusCountsBySource.cap.due_soon || 0;
  const totalOverdue = calOverdue + memOverdue + capOverdue;
  const totalDueSoon = calDueSoon + memDueSoon + capDueSoon;
  const filteredOverdue = statusCountsFiltered.overdue || 0;
  const filteredDueSoon = statusCountsFiltered.due_soon || 0;
  const hasAttention = (sourceFilter === 'all' && (totalOverdue > 0 || totalDueSoon > 0)) ||
    (sourceFilter !== 'all' && (filteredOverdue > 0 || filteredDueSoon > 0));

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: true,
    animation: { duration: 400 },
    plugins: {
      legend: { position: 'bottom', labels: { padding: 14 } },
      tooltip: {
        mode: 'index',
        intersect: false,
        backgroundColor: 'rgba(15, 23, 42, 0.92)',
        padding: 10,
        cornerRadius: 8,
        titleFont: { size: 12, weight: '600' },
        bodyFont: { size: 12 },
      },
    },
  };

  const barOptions = {
    ...chartOptions,
    indexAxis: 'y',
    scales: { x: { beginAtZero: true, ticks: { precision: 0 }, grid: { color: 'rgba(148, 163, 184, 0.2)' } }, y: { grid: { display: false } } },
  };

  const ChartCard = ({ title, subtitle, children, className = '' }) => (
    <div className={`bg-white rounded-2xl border border-slate-200/90 p-5 shadow-md shadow-slate-200/50 ${className}`}>
      <h3 className="text-sm font-semibold text-slate-800 tracking-tight mb-0.5">{title}</h3>
      <p className="text-xs text-slate-500 mb-3">{subtitle}</p>
      <div className="min-h-[200px]">{children}</div>
    </div>
  );

  if (filteredItems.length === 0 && (obligationsWithSource.length > 0 || memosWithSource.length > 0 || capsWithSource.length > 0)) {
    return (
      <div className="space-y-6">
        <SourceFilter value={sourceFilter} onChange={setSourceFilter} />
        <div className="rounded-2xl border border-slate-200 bg-slate-50/50 p-8 text-center text-slate-500 text-sm">
          No items in this selection. Choose another source above.
        </div>
      </div>
    );
  }

  if (obligationsWithSource.length === 0 && memosWithSource.length === 0 && capsWithSource.length === 0) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-slate-50/50 p-8 text-center text-slate-500 text-sm">
        No obligation, memo, or CAP data yet. Charts will appear once you have CMS Calendar obligations, memos, or CAPs.
      </div>
    );
  }

  const buildStatusCombined = () => {
    const data = {
      labels: STATUS_ORDER.map((k) => STATUS_LABELS[k]),
      datasets: [
        { label: SOURCE_LABELS.calendar, data: STATUS_ORDER.map((k) => statusCountsBySource.calendar[k] || 0), backgroundColor: SOURCE_CHART_COLORS.calendar, borderRadius: BAR_RADIUS },
        { label: SOURCE_LABELS.memo, data: STATUS_ORDER.map((k) => statusCountsBySource.memo[k] || 0), backgroundColor: SOURCE_CHART_COLORS.memo, borderRadius: BAR_RADIUS },
        { label: SOURCE_LABELS.cap, data: STATUS_ORDER.map((k) => statusCountsBySource.cap[k] || 0), backgroundColor: SOURCE_CHART_COLORS.cap, borderRadius: BAR_RADIUS },
      ],
    };
    return (
      <Bar
        data={data}
        options={{
          ...barOptions,
          plugins: { ...barOptions.plugins, legend: { position: 'top' } },
        }}
        aria-label="Status by source (grouped)"
      />
    );
  };

  const buildStatusSingle = () => {
    const data = {
      labels: STATUS_ORDER.map((k) => STATUS_LABELS[k]),
      datasets: [{
        data: STATUS_ORDER.map((k) => statusCountsFiltered[k] || 0),
        backgroundColor: STATUS_ORDER.map((k) => STATUS_COLORS[k]),
        borderWidth: 0,
      }],
    };
    return (
      <Doughnut
        data={data}
        options={{ ...chartOptions, cutout: '58%', plugins: { ...chartOptions.plugins, legend: { position: 'bottom' } } }}
        aria-label="Status breakdown"
      />
    );
  };

  const buildOverdueByTypeBar = (typeMap, label) => {
    const labels = Object.keys(typeMap);
    return (
      <Bar
        data={{
          labels: labels.length ? labels : ['No at-risk items'],
          datasets: [
            { label: 'Overdue', data: labels.map((t) => typeMap[t].overdue), backgroundColor: 'rgba(185, 28, 28, 0.88)', borderRadius: BAR_RADIUS },
            { label: `Due within ${DUE_SOON_DAYS} days`, data: labels.map((t) => typeMap[t].due_soon), backgroundColor: 'rgba(180, 83, 9, 0.88)', borderRadius: BAR_RADIUS },
          ],
        }}
        options={{ ...barOptions, plugins: { ...barOptions.plugins, legend: { position: 'top' } } }}
        aria-label={`Areas of concern — ${label}`}
      />
    );
  };

  const buildPersistentBar = (entries, label) => {
    const labels = entries.length ? entries.map(([t]) => t) : ['No overdue items'];
    const data = entries.length ? entries.map(([, n]) => n) : [0];
    return (
      <Bar
        data={{
          labels,
          datasets: [{ label: 'Overdue', data, backgroundColor: 'rgba(185, 28, 28, 0.88)', borderRadius: BAR_RADIUS }],
        }}
        options={barOptions}
        aria-label={`Persistent non-compliance — ${label}`}
      />
    );
  };

  const filterLabel = sourceFilter === 'all' ? 'All sources' : sourceFilter === 'calendar' ? SOURCE_LABELS.calendar : sourceFilter === 'memo' ? SOURCE_LABELS.memo : SOURCE_LABELS.cap;

  const totalItems = (sourceFilter === 'all' ? obligationsWithSource.length + memosWithSource.length + capsWithSource.length : filteredItems.length);
  const kpiOverdue = showCombined ? totalOverdue : filteredOverdue;
  const kpiDueSoon = showCombined ? totalDueSoon : filteredDueSoon;

  return (
    <div className="space-y-8" aria-label="Leadership insights charts">
      <SourceFilter value={sourceFilter} onChange={setSourceFilter} />

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="rounded-2xl border border-slate-200/90 bg-white p-5 shadow-md shadow-slate-200/50">
          <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-1">Overdue</p>
          <p className="text-3xl font-bold text-slate-800 tracking-tight">{kpiOverdue}</p>
          <div className="mt-2 h-1.5 w-full rounded-full bg-slate-100 overflow-hidden">
            <div className="h-full rounded-full bg-red-600" style={{ width: totalItems ? `${Math.min(100, (kpiOverdue / totalItems) * 100)}%` : '0%' }} aria-hidden />
          </div>
        </div>
        <div className="rounded-2xl border border-slate-200/90 bg-white p-5 shadow-md shadow-slate-200/50">
          <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-1">Due within {DUE_SOON_DAYS} days</p>
          <p className="text-3xl font-bold text-slate-800 tracking-tight">{kpiDueSoon}</p>
          <div className="mt-2 h-1.5 w-full rounded-full bg-slate-100 overflow-hidden">
            <div className="h-full rounded-full bg-amber-500" style={{ width: totalItems ? `${Math.min(100, (kpiDueSoon / totalItems) * 100)}%` : '0%' }} aria-hidden />
          </div>
        </div>
        <div className="rounded-2xl border border-slate-200/90 bg-white p-5 shadow-md shadow-slate-200/50">
          <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-1">Total items</p>
          <p className="text-3xl font-bold text-slate-800 tracking-tight">{totalItems}</p>
          <div className="mt-2 h-1.5 w-full rounded-full bg-slate-100 overflow-hidden">
            <div className="h-full rounded-full bg-indigo-500" style={{ width: '100%' }} aria-hidden />
          </div>
        </div>
      </div>

      {hasAttention && (
        <div
          className={`rounded-2xl border px-4 py-3.5 text-sm font-medium ${
            (showCombined ? totalOverdue : filteredOverdue) > 0
              ? 'bg-red-50/90 border-red-200 text-red-800'
              : 'bg-amber-50/90 border-amber-200 text-amber-800'
          }`}
          role="alert"
        >
          <span className="font-semibold">
            {showCombined ? (
              <>
                {totalOverdue > 0 && `${totalOverdue} overdue (${calOverdue} CMS Calendar, ${memOverdue} Memos, ${capOverdue} CAP)`}
                {totalOverdue > 0 && totalDueSoon > 0 && ' · '}
                {totalDueSoon > 0 && `${totalDueSoon} due within ${DUE_SOON_DAYS} days (${calDueSoon} CMS Calendar, ${memDueSoon} Memos, ${capDueSoon} CAP)`}
              </>
            ) : (
              <>
                {filteredOverdue > 0 && `${filteredOverdue} overdue`}
                {filteredOverdue > 0 && filteredDueSoon > 0 && ' · '}
                {filteredDueSoon > 0 && `${filteredDueSoon} due within ${DUE_SOON_DAYS} days`}
              </>
            )}
          </span>
          {' — Charts below reflect your selection.'}
        </div>
      )}

      {/* Status: combined (grouped bar) vs single (doughnut) */}
      <section>
        <h2 className="text-lg font-semibold text-slate-800 tracking-tight mb-1">Status at a glance</h2>
        <p className="text-sm text-slate-500 mb-4">
          {showCombined
            ? 'Totals by status with CMS Calendar vs Memos side by side for direct comparison.'
            : `${filterLabel} only. Red = overdue, amber = due soon.`}
        </p>
        <div className="max-w-2xl">
          <ChartCard
            title={showCombined ? 'Status by source (grouped)' : `Status — ${filterLabel}`}
            subtitle={showCombined ? 'Compare calendar and memo counts per status.' : 'Current status breakdown.'}
          >
            {showCombined ? buildStatusCombined() : buildStatusSingle()}
          </ChartCard>
        </div>
      </section>

      {/* Areas of concern: when All = two charts (different type systems); when single = one */}
      <section>
        <h2 className="text-lg font-semibold text-slate-800 tracking-tight mb-1">Areas of concern by type</h2>
        <p className="text-sm text-slate-500 mb-4">
          Overdue and due-soon by type. {showCombined ? 'Shown separately because Calendar and Memos use different type systems.' : `Filtered to ${filterLabel}.`}
        </p>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {(showCombined || sourceFilter === 'calendar') && (
            <ChartCard
              title={showCombined ? `${SOURCE_LABELS.calendar} — by obligation type` : 'By obligation type'}
              subtitle="Which calendar types are out of compliance or close to due."
            >
              {buildOverdueByTypeBar(overdueDueSoonByTypeCalendar, SOURCE_LABELS.calendar)}
            </ChartCard>
          )}
          {(showCombined || sourceFilter === 'memo') && (
            <ChartCard
              title={showCombined ? `${SOURCE_LABELS.memo} — by classification` : 'By memo classification'}
              subtitle="Which memo types are overdue or due soon."
            >
              {buildOverdueByTypeBar(overdueDueSoonByTypeMemos, SOURCE_LABELS.memo)}
            </ChartCard>
          )}
          {(showCombined || sourceFilter === 'cap') && (
            <ChartCard
              title={showCombined ? `${SOURCE_LABELS.cap} — by type` : 'By CAP type'}
              subtitle="Which CAP types are overdue or due soon."
            >
              {buildOverdueByTypeBar(overdueDueSoonByTypeCap, SOURCE_LABELS.cap)}
            </ChartCard>
          )}
        </div>
      </section>

      {/* Accountability: separate by design (assignee vs department); show by filter */}
      <section>
        <h2 className="text-lg font-semibold text-slate-800 tracking-tight mb-1">Accountability</h2>
        <p className="text-sm text-slate-500 mb-4">
          Who owns what and who is at risk. Calendar = assignees; Memos = departments. Shown by your selection.
        </p>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {(showCombined || sourceFilter === 'calendar') && (
            <>
              <ChartCard title="Calendar — who owns what" subtitle="Obligations per assignee. Unassigned needs an owner.">
                <Bar
                  data={{
                    labels: Object.keys(byAssignee).length ? Object.keys(byAssignee) : ['No assignments'],
                    datasets: [{ label: 'Obligations', data: Object.keys(byAssignee).map((a) => byAssignee[a]), backgroundColor: SOURCE_CHART_COLORS.calendar, borderRadius: BAR_RADIUS }],
                  }}
                  options={barOptions}
                  aria-label="Obligations by assignee"
                />
              </ChartCard>
              <ChartCard title="Calendar — who is at risk" subtitle="Overdue and due-soon by assignee. Follow up with these owners.">
                <Bar
                  data={{
                    labels: Object.keys(overdueAtRiskByAssignee).length ? Object.keys(overdueAtRiskByAssignee) : ['No at-risk'],
                    datasets: [
                      { label: 'Overdue', data: Object.keys(overdueAtRiskByAssignee).map((a) => overdueAtRiskByAssignee[a].overdue), backgroundColor: 'rgba(185, 28, 28, 0.88)', borderRadius: BAR_RADIUS },
                      { label: `Due within ${DUE_SOON_DAYS} days`, data: Object.keys(overdueAtRiskByAssignee).map((a) => overdueAtRiskByAssignee[a].due_soon), backgroundColor: 'rgba(180, 83, 9, 0.88)', borderRadius: BAR_RADIUS },
                    ],
                  }}
                  options={{ ...barOptions, plugins: { ...barOptions.plugins, legend: { position: 'top' } } }}
                  aria-label="At risk by assignee"
                />
              </ChartCard>
            </>
          )}
          {(showCombined || sourceFilter === 'memo') && (
            <>
              <ChartCard title="Memos — who owns what (by department)" subtitle="Memos per department.">
                <Bar
                  data={{
                    labels: Object.keys(memosByDepartment).length ? Object.keys(memosByDepartment) : ['No departments'],
                    datasets: [{ label: 'Memos', data: Object.keys(memosByDepartment).map((d) => memosByDepartment[d]), backgroundColor: SOURCE_CHART_COLORS.memo, borderRadius: BAR_RADIUS }],
                  }}
                  options={barOptions}
                  aria-label="Memos by department"
                />
              </ChartCard>
              <ChartCard title="Memos — who is at risk (by department)" subtitle="Overdue and due-soon memos by department.">
                <Bar
                  data={{
                    labels: Object.keys(memosAtRiskByDepartment).length ? Object.keys(memosAtRiskByDepartment) : ['No at-risk'],
                    datasets: [
                      { label: 'Overdue', data: Object.keys(memosAtRiskByDepartment).map((d) => memosAtRiskByDepartment[d].overdue), backgroundColor: 'rgba(185, 28, 28, 0.88)', borderRadius: BAR_RADIUS },
                      { label: `Due within ${DUE_SOON_DAYS} days`, data: Object.keys(memosAtRiskByDepartment).map((d) => memosAtRiskByDepartment[d].due_soon), backgroundColor: 'rgba(180, 83, 9, 0.88)', borderRadius: BAR_RADIUS },
                    ],
                  }}
                  options={{ ...barOptions, plugins: { ...barOptions.plugins, legend: { position: 'top' } } }}
                  aria-label="Memos at risk by department"
                />
              </ChartCard>
            </>
          )}
          {(showCombined || sourceFilter === 'cap') && (
            <>
              <ChartCard title="CAP — who owns what" subtitle="CAP items per owner.">
                <Bar
                  data={{
                    labels: Object.keys(byAssigneeCap).length ? Object.keys(byAssigneeCap) : ['No assignments'],
                    datasets: [{ label: 'CAP items', data: Object.keys(byAssigneeCap).map((a) => byAssigneeCap[a]), backgroundColor: SOURCE_CHART_COLORS.cap, borderRadius: BAR_RADIUS }],
                  }}
                  options={barOptions}
                  aria-label="CAP items by owner"
                />
              </ChartCard>
              <ChartCard title="CAP — who is at risk" subtitle="Overdue and due-soon CAP items by owner.">
                <Bar
                  data={{
                    labels: Object.keys(overdueAtRiskByAssigneeCap).length ? Object.keys(overdueAtRiskByAssigneeCap) : ['No at-risk'],
                    datasets: [
                      { label: 'Overdue', data: Object.keys(overdueAtRiskByAssigneeCap).map((a) => overdueAtRiskByAssigneeCap[a].overdue), backgroundColor: 'rgba(185, 28, 28, 0.88)', borderRadius: BAR_RADIUS },
                      { label: `Due within ${DUE_SOON_DAYS} days`, data: Object.keys(overdueAtRiskByAssigneeCap).map((a) => overdueAtRiskByAssigneeCap[a].due_soon), backgroundColor: 'rgba(180, 83, 9, 0.88)', borderRadius: BAR_RADIUS },
                    ],
                  }}
                  options={{ ...barOptions, plugins: { ...barOptions.plugins, legend: { position: 'top' } } }}
                  aria-label="CAP at risk by owner"
                />
              </ChartCard>
            </>
          )}
        </div>
      </section>

      {/* Due by month: combined = stacked (totals + composition); single = one series */}
      <section>
        <h2 className="text-lg font-semibold text-slate-800 tracking-tight mb-1">Upcoming workload and backlog</h2>
        <p className="text-sm text-slate-500 mb-4">
          {showCombined
            ? 'Stacked view: total items due each month with Calendar vs Memo breakdown. Past due = backlog.'
            : `${filterLabel} — items due by month. Past due = backlog.`}
        </p>
        <ChartCard title={showCombined ? 'Due by month (by source)' : `Due by month — ${filterLabel}`} subtitle={showCombined ? 'Blue = CMS Calendar, purple = Memos, green = CAP.' : 'Count per month.'}>
          {showCombined ? (
            <Bar
              data={{
                labels: dueByMonthBySource.monthKeys,
                datasets: [
                  { label: SOURCE_LABELS.calendar, data: dueByMonthBySource.monthKeys.map((k) => dueByMonthBySource.calendar[k]), backgroundColor: SOURCE_CHART_COLORS.calendar, borderRadius: BAR_RADIUS },
                  { label: SOURCE_LABELS.memo, data: dueByMonthBySource.monthKeys.map((k) => dueByMonthBySource.memo[k]), backgroundColor: SOURCE_CHART_COLORS.memo, borderRadius: BAR_RADIUS },
                  { label: SOURCE_LABELS.cap, data: dueByMonthBySource.monthKeys.map((k) => dueByMonthBySource.cap[k]), backgroundColor: SOURCE_CHART_COLORS.cap, borderRadius: BAR_RADIUS },
                ],
              }}
              options={{
                ...chartOptions,
                plugins: { ...chartOptions.plugins, legend: { position: 'top' } },
                scales: { x: { grid: { display: false }, stacked: true }, y: { beginAtZero: true, stacked: true, ticks: { precision: 0 }, grid: { color: 'rgba(148, 163, 184, 0.2)' } } },
              }}
              aria-label="Due by month stacked by source"
            />
          ) : (
            <Bar
              data={{
                labels: dueByMonthFiltered.monthKeys,
                datasets: [{
                  label: 'Items due',
                  data: dueByMonthFiltered.monthKeys.map((k) => dueByMonthFiltered.buckets[k]),
                  backgroundColor: dueByMonthFiltered.monthKeys.map((k) => k === 'Past due' ? 'rgba(185, 28, 28, 0.88)' : 'rgba(37, 99, 235, 0.88)'),
                  borderRadius: BAR_RADIUS,
                }],
              }}
              options={{
                ...chartOptions,
                scales: { x: { grid: { display: false } }, y: { beginAtZero: true, ticks: { precision: 0 }, grid: { color: 'rgba(148, 163, 184, 0.2)' } } },
              }}
              aria-label="Due by month"
            />
          )}
        </ChartCard>
      </section>

      {/* Persistent non-compliance: when All = two charts; when single = one */}
      <section>
        <h2 className="text-lg font-semibold text-slate-800 tracking-tight mb-1">Persistent non-compliance by type</h2>
        <p className="text-sm text-slate-500 mb-4">
          Types with the most overdue items. {showCombined ? 'Calendar and Memos use different type systems, so shown separately.' : `Filtered to ${filterLabel}.`}
        </p>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {(showCombined || sourceFilter === 'calendar') && (
            <ChartCard title={showCombined ? `${SOURCE_LABELS.calendar} — top overdue by type` : 'Top overdue by obligation type'} subtitle="Prioritize these calendar types.">
              {buildPersistentBar(persistentByTypeCalendar, SOURCE_LABELS.calendar)}
            </ChartCard>
          )}
          {(showCombined || sourceFilter === 'memo') && (
            <ChartCard title={showCombined ? `${SOURCE_LABELS.memo} — top overdue by type` : 'Top overdue by classification'} subtitle="Prioritize these memo types.">
              {buildPersistentBar(persistentByTypeMemos, SOURCE_LABELS.memo)}
            </ChartCard>
          )}
          {(showCombined || sourceFilter === 'cap') && (
            <ChartCard title={showCombined ? `${SOURCE_LABELS.cap} — top overdue by type` : 'Top overdue by CAP type'} subtitle="Prioritize these CAP types.">
              {buildPersistentBar(persistentByTypeCap, SOURCE_LABELS.cap)}
            </ChartCard>
          )}
        </div>
      </section>
    </div>
  );
}

function SourceFilter({ value, onChange }) {
  const options = [
    { id: 'all', label: 'All sources' },
    { id: 'calendar', label: 'CMS Calendar' },
    { id: 'memo', label: 'Memos' },
    { id: 'cap', label: 'CAP' },
  ];
  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="text-sm font-medium text-slate-600 mr-1">Show charts for:</span>
      <div
        className="inline-flex rounded-xl bg-slate-100/80 p-1 border border-slate-200/80"
        role="tablist"
        aria-label="Chart source filter"
      >
        {options.map((opt) => (
          <button
            key={opt.id}
            type="button"
            role="tab"
            aria-selected={value === opt.id}
            onClick={() => onChange(opt.id)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-indigo-400 ${
              value === opt.id
                ? 'bg-white text-slate-800 shadow-sm border border-slate-200/80'
                : 'text-slate-600 hover:text-slate-800'
            }`}
          >
            {opt.label}
          </button>
        ))}
      </div>
    </div>
  );
}
